import { useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { LinearProgress, Button, Typography } from "@material-ui/core";
import Cropper from "react-easy-crop";

import getCroppedImg from "./cropImageUtil";
import { EditPencilIcon } from "../../icons/EditPencilIcon";
import { Modal } from "../Modal";
import { FadeMenu } from "../fadeMenu";
import { updateAvatarProfile } from "./service";
import { useStyles } from "./styles";

export const CropImage = ({
  title,
  avatarParticipant,
  participantProfileId,
  onUploadedImage,
}) => {
  const classes = useStyles();
  const [openModal, setOpenModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [currentAvatar, setCurrentAvatar] = useState(avatarParticipant);
  const [loading, setLoading] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [fileCroppedImage, setFileCroppedImage] = useState(null);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [errorsForm, setErrorsForm] = useState([]);
  const onCropComplete = useCallback((croppedArea, croppedAreaInPixels) => {
    setCroppedAreaPixels(croppedAreaInPixels);
  }, []);

  const resetErrors = () => {
    setErrorsForm([]);
  };

  const setErrorForm = (key, message) => {
    setErrorsForm((prevState) => {
      return [...prevState, { key, message }];
    });
  };

  const handleChange = (event) => {
    resetErrors();
    const [image] = event.target.files;
    if (/\.(jpe?g|bmp|tiff|png)$/i.test(image.name) === false) {
      setErrorForm(
        "img",
        "Please select a valid Format (JPEG, JPG, BMP, TIFF, PNG)."
      );
    } else {
      setCurrentImage(URL.createObjectURL(image));
    }
  };

  const showCroppedImage = useCallback(async () => {
    try {
      const { url, file } = await getCroppedImg(
        currentImage,
        croppedAreaPixels
      );
      setCroppedImage(url);
      setFileCroppedImage(file);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, currentImage]);

  const clearModal = () => {
    setLoading(false);
    setOpenModal(false);
    setCurrentImage(null);
    setCroppedImage(null);
    setFileCroppedImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    resetErrors();
  };

  useEffect(() => {
    setCurrentAvatar(avatarParticipant);
  }, [avatarParticipant]);

  const handleUpdatePhoto = () => {
    if (croppedImage === null) {
      showCroppedImage();
    } else {
      const data = new FormData();
      data.append("participantProfileId", participantProfileId);
      data.append("profilePicture", fileCroppedImage);
      setLoading(true);
      updateAvatarProfile(data)
        .then((res) => {
          setCurrentAvatar(croppedImage);
          onUploadedImage(res?.data?.data?.profilePicturePath);
          clearModal();
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  const handleDeleteOnModal = () => {
    const data = new FormData();

    data.append("participantProfileId", participantProfileId);
    setLoading(true);
    updateAvatarProfile(data)
      .then(() => {
        setCurrentAvatar(null);
        onUploadedImage(null);
        setOpenDeleteModal(false);
        clearModal();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <div
        className={clsx(classes.fileWrapper, classes.fileWrapperNewParticipant)}
      >
        <span className={clsx("MuiFormLabel-root", classes.titlePicture)}>
          {title}
        </span>
        <div>&nbsp;</div>
        <div
          className={clsx(classes.fileContainer, classes.fileWrapperRounded)}
        >
          <div className={classes.avatar}>
            <img src={currentAvatar} alt="" />
          </div>
          {currentAvatar === null ? (
            <Button
              color="primary"
              data-testid="upload-btn"
              className={classes.fileButton}
              onClick={() => setOpenModal(true)}
            >
              Upload
            </Button>
          ) : (
            <Button
              className={clsx(
                classes.fileButtonEdit,
                classes.fileButtonEditNewParticipants
              )}
              color="primary"
            >
              <FadeMenu
                classNameLabel={classes.editandRemove}
                menuProps={{
                  "data-testid": "image-options-menu",
                }}
                options={[
                  {
                    label: "Edit",
                    action: () => setOpenModal(true),
                  },
                  {
                    label: "Remove",
                    action: () => setOpenDeleteModal(true),
                  },
                ]}
              >
                <EditPencilIcon />
              </FadeMenu>
            </Button>
          )}
        </div>
      </div>
      <Modal
        open={openModal}
        onClose={clearModal}
        onConfirm={handleUpdatePhoto}
        title="Add Profile Picture"
        cancelButtonText="Cancel"
        confirmButtonText="Save"
        actionConfirmDisabled={currentImage === null}
        actionsDisabled={loading}
        customCssCancelBtn={classes.cancelButton}
        customCssConfirmBtn={classes.updateBottom}
        customCssDialogActions={classes.dialogAction}
      >
        <div className={classes.root}></div>
        {loading && (
          <div className={classes.root}>
            <LinearProgress color="secondary" />
          </div>
        )}
        {croppedImage !== null ? (
          <div className={classes.imageCropResult}>
            <img src={croppedImage} alt="" />
          </div>
        ) : (
          <>
            {currentImage !== null ? (
              <>
                <div className={classes.cropImageContainer}>
                  <Cropper
                    image={currentImage}
                    crop={crop}
                    zoom={zoom}
                    cropShape="round"
                    cropSize={{ width: 300, height: 300 }}
                    aspect={1}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                <div className={classes.fileButtonContainer}>
                  <label
                    className={clsx(
                      classes.changeFileButton,
                      classes.changeImageNewParticipants
                    )}
                    htmlFor="change-avatar-profile"
                  >
                    Change Picture
                    <input
                      accept="image/*"
                      type="file"
                      label="change-avatar-profile"
                      id="change-avatar-profile"
                      name="change-avatar-profile"
                      className={classes.fileInput}
                      onChange={handleChange}
                    />
                  </label>
                </div>
              </>
            ) : (
              <div
                className={clsx(classes.fileWrapper, classes.fileWrapperModal)}
              >
                <div
                  className={clsx(
                    classes.fileContainer,
                    classes.fileContainerNewParticipant
                  )}
                >
                  <div
                    className={clsx(
                      classes.imageOverlay,
                      classes.imageOverlayNewParticipants
                    )}
                  />
                  <label
                    className={clsx(
                      classes.fileButton,
                      classes.uploadImageButton
                    )}
                    htmlFor="avatar-profile"
                  >
                    Upload File
                    <input
                      accept="image/*"
                      type="file"
                      label="avatar-profile"
                      id="avatar-profile"
                      name="avatar-profile"
                      className={classes.fileInput}
                      data-testid="file-input"
                      onChange={handleChange}
                    />
                  </label>
                </div>
              </div>
            )}
          </>
        )}
        {errorsForm.length > 0 && (
          <>
            {errorsForm.map((error) => (
              <div>
                {error.message.length > 0 ? (
                  <Typography
                    align="left"
                    variant="body2"
                    className={classes.errorMessage}
                  >
                    Invalid file. {error.message}
                  </Typography>
                ) : null}
              </div>
            ))}
          </>
        )}
      </Modal>
      <Modal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onConfirm={() => handleDeleteOnModal()}
        title="Delete Participant Profile Picture"
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
      >
        {loading && (
          <div className={classes.root}>
            <LinearProgress color="secondary" />
          </div>
        )}
        <span>
          You are about to delete your profile picture. Are you sure you want to
          continue?
        </span>
      </Modal>
    </>
  );
};

CropImage.propTypes = {
  title: PropTypes.string,
  avatarParticipant: PropTypes.string,
  participantProfileId: PropTypes.string,
  onUploadedImage: PropTypes.func,
};

CropImage.defaultProps = {
  title: "Profile Picture",
  avatarParticipant: "",
  participantProfileId: "",
  onUploadedImage: (f) => f,
};
